import { colors } from '../theme';
import React, { useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Feed from './Feed';
import { getProductImageSizes } from '../utils/product';

function ShowcaseSkeletonCard() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 1,
        border: '1px solid var(--dxd-border)',
        backgroundColor: colors.surface,
      }}
    >
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: '100%',
          height: 'auto',
          aspectRatio: '1 / 1',
          backgroundColor: colors.elevated,
        }}
      />
      <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
        <Skeleton animation="wave" width="86%" height={40} />
        <Skeleton animation="wave" width="64%" height={14} sx={{ mt: 0.35 }} />
        <Skeleton animation="wave" width="45%" height={24} sx={{ mt: 0.75 }} />
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
          <Skeleton animation="wave" variant="rounded" width={40} height={44} />
        </Box>
      </Box>
    </Box>
  );
}

function HorizontalShowcase({
  title,
  products = [],
  loading = false,
  priorityFirstImage = false,
  eagerImageCount = 0,
  itemCount = 4,
  compact = false,
  getTopRank,
}) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;

    const amount =
      window.innerWidth < 600 ? (window.innerWidth - 32 - 12) / 2 : 230;

    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  if (!loading && !products.length) return null;

  return (
    <Box sx={{ mb: { xs: 0, lg: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 1, md: 1.8 },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: colors.accent,
              fontWeight: 900,
              letterSpacing: 1.2,
              fontSize: { xs: '0.72rem', md: '0.8rem' },
            }}
          >
            NOVEDADES
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 400,
              color: colors.text,
              lineHeight: 1.05,
              fontSize: { xs: '1.55rem', sm: '1.85rem', md: '2.1rem' },
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1 }}>
          <IconButton
            aria-label={`Ver discos anteriores en ${title}`}
            onClick={() => scroll('left')}
            sx={{
              color: colors.text,
              border: '1px solid var(--dxd-border)',
              backgroundColor: colors.surface,
              width: { xs: 34, md: 38 },
              height: { xs: 34, md: 38 },
              '&:hover': {
                borderColor: colors.accent,
                backgroundColor: colors.tint,
              },
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          <IconButton
            aria-label={`Ver mas discos en ${title}`}
            onClick={() => scroll('right')}
            sx={{
              color: colors.text,
              border: '1px solid var(--dxd-border)',
              backgroundColor: colors.surface,
              width: { xs: 34, md: 38 },
              height: { xs: 34, md: 38 },
              '&:hover': {
                borderColor: colors.accent,
                backgroundColor: colors.tint,
              },
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          display: { xs: 'flex', md: 'grid' },
          gridTemplateColumns: { md: 'repeat(4, minmax(0, 1fr))' },
          gap: { xs: 1.2, sm: 1.6 },
          overflowX: { xs: 'auto', md: 'visible' },
          scrollBehavior: 'smooth',
          pb: { xs: 1, md: 0 },
          pr: { xs: 1, sm: 0 },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: colors.elevated,
            borderRadius: 10,
          },
          '&::-webkit-scrollbar-thumb': {
            background: colors.controlBorder,
            borderRadius: 10,
          },
          '@media (min-width: 1440px)': {
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            gap: 1.5,
          },
        }}
      >
        {loading
          ? Array.from({ length: itemCount }, (_, item) => (
              <Box
                key={`showcase-skeleton-${title}-${item}`}
                sx={{
                  minWidth: {
                    xs: 'calc((100vw - 32px - 12px) / 2)',
                    sm: 220,
                    md: 0,
                  },
                  maxWidth: {
                    xs: 'calc((100vw - 32px - 12px) / 2)',
                    sm: 220,
                    md: 'none',
                  },
                  display: 'flex',
                  flex: '0 0 auto',
                  scrollSnapAlign: 'start',
                }}
              >
                <ShowcaseSkeletonCard />
              </Box>
            ))
          : products.slice(0, itemCount).map((product, index) => (
              <Box
                key={product._id}
                sx={{
                  minWidth: {
                    xs: 'calc((100vw - 32px - 12px) / 2)',
                    sm: 220,
                    md: 0,
                  },
                  maxWidth: {
                    xs: 'calc((100vw - 32px - 12px) / 2)',
                    sm: 220,
                    md: 'none',
                  },
                  display: 'flex',
                  flex: '0 0 auto',
                  scrollSnapAlign: 'start',
                }}
              >
                <Feed
                  product={product}
                  mode="catalog"
                  priority={priorityFirstImage && index === 0}
                  eagerImage={index < eagerImageCount}
                  imageSizes={getProductImageSizes('showcase')}
                  compact={compact}
                  topRank={getTopRank?.(product)}
                />
              </Box>
            ))}
      </Box>
    </Box>
  );
}

export default HorizontalShowcase;
